import { useRef, useState } from "react";
import { Row, Col, Form, Input, InputNumber, Select, Button, message, Alert, Popconfirm, Card, Tag, Space, Tooltip } from "antd";
import { SaveOutlined, DeleteOutlined, WarningOutlined } from "@ant-design/icons";
import { actualizarProductoOcr, eliminarProductoOcr, buscarProductos } from "../../services/ocrActas/OcrEntregasService";
import { CompararOcrBd, BloqueResuelto } from "./ocrActasBloques";
import { formatMoneda } from "./ocrEntregasUtils";
import { BANDERAS_PRODUCTO } from "./ocrEntregasBanderas";

const DEBOUNCE_MS = 400;

// Valor de solo lectura (PO, valor unitario, valor total, saldo): vienen de
// fin_productos o se calculan, nunca los escribe el usuario a mano.
const ValorBloqueado = ({ label, valor, moneda }) => (
  <div>
    <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 6, fontWeight: 500 }}>{label}</div>
    <div
      style={{
        minHeight: 32,
        display: "flex",
        alignItems: "center",
        padding: "0 12px",
        background: "#fafafa",
        border: "1px solid #f0f0f0",
        borderRadius: 6,
        color: "#595959",
      }}
    >
      {valor === null || valor === undefined ? "—" : moneda ? formatMoneda(valor) : valor}
    </div>
  </div>
);

// Cada producto de una acta es una fila independiente en ocr_productos, con
// su propio oid. Este bloque edita y guarda UN producto a la vez. PO, valor
// unitario, valor total y saldo son de solo lectura: el backend los calcula
// siempre desde fin_productos y el stock disponible (ver
// actaMatchingService.calcularSaldoDisponible), nunca se confia en lo que el
// formulario mande para esos campos.
const ProductoItemEditable = ({ item, oidProyecto, titulo, permissionUpdate, puedeEliminar, onGuardado, onEliminado }) => {
  const [form] = Form.useForm();
  const [guardando, setGuardando] = useState(false);
  const [eliminando, setEliminando] = useState(false);
  const [opcionesProducto, setOpcionesProducto] = useState([]);
  const [buscandoProducto, setBuscandoProducto] = useState(false);
  const debounceRef = useRef(null);

  const oidProducto = Form.useWatch("oid_producto", form);
  const nombreProductoBd = Form.useWatch("nombre_producto_bd", form);

  const [datosBloqueados, setDatosBloqueados] = useState({
    numero_po: item.numero_po,
    valor_unitario: item.valor_unitario,
    valor_total: item.valor_total,
    saldo: item.saldo,
  });

  const valoresIniciales = {
    oid_producto: item.oid_producto,
    nombre_producto_bd: item.nombre_producto_bd,
    cantidad: item.cantidad,
  };

  const handleBuscarProductos = (texto) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!texto || texto.trim().length < 2) {
      setOpcionesProducto([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setBuscandoProducto(true);
      try {
        const respuesta = await buscarProductos(oidProyecto, texto.trim());
        setOpcionesProducto(respuesta.data || []);
      } catch {
        setOpcionesProducto([]);
      } finally {
        setBuscandoProducto(false);
      }
    }, DEBOUNCE_MS);
  };

  const handleSeleccionarProducto = (oidSeleccionado) => {
    const producto = opcionesProducto.find((p) => p.oid === oidSeleccionado);
    if (!producto) return;
    form.setFieldsValue({ oid_producto: producto.oid, nombre_producto_bd: producto.nombre_producto });
    setDatosBloqueados({
      numero_po: producto.Numero_PO,
      valor_unitario: producto.valor_unitario_COP,
      valor_total: null,
      saldo: producto.disponible,
    });
  };

  const handleQuitarProducto = () => {
    // Importante: tambien hay que limpiar nombre_producto_bd. Si solo se
    // borra oid_producto, el campo "Nombre del producto (manual)" queda con
    // el texto del match anterior (no vacio), lo que confunde: parece que
    // no tomo la nueva busqueda cuando en realidad nunca se borro el valor viejo.
    form.setFieldsValue({ oid_producto: null, nombre_producto_bd: null });
    setOpcionesProducto([]);
    setDatosBloqueados({ numero_po: null, valor_unitario: null, valor_total: null, saldo: null });
  };

  const handleGuardar = async () => {
    if (!permissionUpdate) {
      message.warning("No tiene permisos para editar este registro");
      return;
    }
    try {
      const valores = await form.validateFields();
      setGuardando(true);
      const actualizado = await actualizarProductoOcr(item.oid, valores);
      message.success(`${titulo}: guardado correctamente`);
      setDatosBloqueados({
        numero_po: actualizado.numero_po,
        valor_unitario: actualizado.valor_unitario,
        valor_total: actualizado.valor_total,
        saldo: actualizado.saldo,
      });
      onGuardado?.(actualizado);
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.response?.data?.error || "Error al guardar los cambios");
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async () => {
    setEliminando(true);
    try {
      await eliminarProductoOcr(item.oid);
      message.success(`${titulo}: eliminado correctamente`);
      onEliminado?.(item.oid);
    } catch (error) {
      message.error(error.response?.data?.error || "Error al eliminar el producto");
    } finally {
      setEliminando(false);
    }
  };

  const banderasActivas = BANDERAS_PRODUCTO.filter((b) => item[b.key]);
  const tieneAlerta = banderasActivas.length > 0;

  return (
    <Card
      size="small"
      title={
        <Space size={8}>
          <span style={{ color: "#262626" }}>{titulo}</span>
          {tieneAlerta && <WarningOutlined style={{ color: "#d4380d" }} />}
        </Space>
      }
      style={{
        marginBottom: 24,
        borderColor: "#e8e8e8",
        borderLeft: tieneAlerta ? "4px solid #ff7875" : "4px solid #d9d9d9",
        borderRadius: 8,
        background: "#ffffff",
      }}
      styles={{ body: { padding: 20 }, header: { background: "#fafafa", borderRadius: "8px 8px 0 0" } }}
      extra={
        puedeEliminar &&
        permissionUpdate && (
          <Popconfirm
            title="¿Eliminar este producto de la acta?"
            description="Quedará registrado en el histórico."
            okText="Eliminar"
            okButtonProps={{ danger: true }}
            onConfirm={handleEliminar}
          >
            <Button size="small" danger type="text" icon={<DeleteOutlined />} loading={eliminando}>
              Eliminar
            </Button>
          </Popconfirm>
        )
      }
    >
      {tieneAlerta && (
        <Space size={[8, 8]} wrap style={{ marginBottom: 16 }}>
          {banderasActivas.map((b) => (
            <Tooltip
              key={b.key}
              title={
                b.key === "bandera_stock_insuficiente" && item.saldo !== null
                  ? `${b.descripcion} Saldo quedaría en ${Number(item.saldo)} unidades.`
                  : b.descripcion
              }
            >
              <Tag color="error">{b.label}</Tag>
            </Tooltip>
          ))}
        </Space>
      )}

      <Form form={form} layout="vertical" initialValues={valoresIniciales} disabled={!permissionUpdate}>
        <Form.Item name="oid_producto" hidden>
          <InputNumber />
        </Form.Item>
        {/* nombre_producto_bd debe quedar SIEMPRE montado (solo se alterna
            "hidden", nunca se desmonta condicionalmente): si el Form.Item se
            monta/desmonta segun oidProducto, Form.useWatch deja de recibir el
            valor inicial cuando esta desmontado, y por eso se veia "Sin
            nombre registrado en BD" aunque la BD si tuviera el nombre. */}
        <Form.Item name="nombre_producto_bd" label="Nombre del producto (manual)" hidden={!!oidProducto}>
          <Input placeholder="Nombre del producto" />
        </Form.Item>

        <CompararOcrBd
          etiquetaOcr="Producto OCR"
          valorOcr={item.nombre_producto_ocr}
          noCoincide={item.bandera_nombre_producto_no_coincide}
        >
          {oidProducto ? (
            // El nombre OCR ya se muestra al lado (columna "Producto OCR");
            // aqui SOLO debe verse el nombre de BD, sin respaldo al de OCR,
            // o se repite el mismo valor en dos lugares cuando nombre_producto_bd
            // este vacio. BloqueResuelto ya maneja ese caso vacio mostrando
            // "Sin nombre registrado en BD" en vez de inventar un dato.
            <BloqueResuelto oid={oidProducto} texto={nombreProductoBd} />
          ) : (
            <Select
              showSearch
              style={{ width: "100%" }}
              placeholder="Escriba para buscar (mín. 2 letras)"
              filterOption={false}
              onSearch={handleBuscarProductos}
              onSelect={handleSeleccionarProducto}
              loading={buscandoProducto}
              notFoundContent={buscandoProducto ? "Buscando..." : "Sin resultados"}
              disabled={!permissionUpdate}
              options={opcionesProducto.map((p) => ({
                value: p.oid,
                label: `${p.nombre_producto} — disponible: ${p.disponible}`,
              }))}
            />
          )}
        </CompararOcrBd>

        {oidProducto ? (
          permissionUpdate && (
            <Button size="small" onClick={handleQuitarProducto} style={{ marginBottom: 20 }}>
              Quitar y buscar de nuevo
            </Button>
          )
        ) : (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Si no aparece en la búsqueda, escriba el nombre manualmente; quedará marcado como producto no encontrado para revisión."
          />
        )}

        <Row gutter={[16, 16]}>
          <Col xs={12} sm={6}>
            <Form.Item label="Cantidad" name="cantidad">
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6}>
            <ValorBloqueado label="N° PO" valor={datosBloqueados.numero_po} />
          </Col>
          <Col xs={12} sm={6}>
            <ValorBloqueado label="Valor unitario" valor={datosBloqueados.valor_unitario} moneda />
          </Col>
          <Col xs={12} sm={6}>
            <ValorBloqueado label="Valor total (calculado)" valor={datosBloqueados.valor_total} moneda />
          </Col>
        </Row>

        <Row gutter={[16, 16]} align="bottom" style={{ marginTop: 16 }}>
          <Col xs={24} sm={8}>
            <ValorBloqueado label="Saldo disponible tras esta entrega" valor={datosBloqueados.saldo} />
            {datosBloqueados.saldo !== null && Number(datosBloqueados.saldo) < 0 && (
              <Tag color="error" style={{ marginTop: 8 }}>
                Sin stock
              </Tag>
            )}
          </Col>
          <Col xs={24} sm={16} style={{ textAlign: "right" }}>
            {permissionUpdate && (
              <Button type="primary" icon={<SaveOutlined />} loading={guardando} onClick={handleGuardar}>
                Guardar producto
              </Button>
            )}
          </Col>
        </Row>
      </Form>
    </Card>
  );
};

export default ProductoItemEditable;
